import { captureException } from "@sentry/react";
import { t } from "i18next";
import { isErrorCode } from "./error-messages";

/**
 * Structured error response from payment-related backend endpoints.
 * @see backend: POST /api/setup-intent, /api/set-default-payment-method, /api/create-order
 */
export interface BackendPaymentErrorResponse {
  error: string;
  message?: string;
  details?: string;
  stripe_code?: string;
  stripe_decline_code?: string;
  retryable?: boolean;
}

/**
 * Parse a failed fetch response and throw with a customer-friendly message.
 * Uses backend's `message` when available, then i18n for known codes, then fallback.
 */
export function parseAndThrowPaymentError(
  errorData: unknown,
  fallbackMessage: string
): never {
  const data = errorData as Partial<BackendPaymentErrorResponse>;
  const displayMessage =
    data?.message ||
    (data?.error && isErrorCode(data.error) ? t(`errors.${data.error}`) : null) ||
    fallbackMessage;

  // Enrich Sentry with structured backend error data
  const sentryError = new Error(displayMessage);
  if (data?.error || data?.details || data?.stripe_code) {
    captureException(sentryError, {
      extra: {
        backend_error: data.error,
        backend_details: data.details,
        stripe_code: data.stripe_code,
        stripe_decline_code: data.stripe_decline_code,
        retryable: data.retryable,
      },
    });
  }

  throw new Error(displayMessage);
}
